import {testIdProps} from '@github-ui/test-id-props'
import {Box} from '@primer/react'
import type {BetterSystemStyleObject} from '@primer/react/lib-esm/sx'

import {SanitizedHtml} from './dom/sanitized-html'

export interface SuggestionsListItemProps {
  renderItem?: () => JSX.Element
  value: string
  asHTML: boolean
  selected: boolean
  onSelect: () => void
  selectedItemRef: React.RefObject<HTMLLIElement> | null
  testId: string
  id: string
}

const suggestionsListItemStyle: BetterSystemStyleObject = {
  position: 'relative',
  bg: 'canvas.overlay',
  lineHeight: 1.5,
  py: '6px',
  px: 2,
  my: 0,
  mx: 2,
  fontSize: 1,
  borderRadius: 2,
  color: 'fg.default',
  cursor: 'pointer',
  height: '32px',
  textOverflow: 'ellipsis',
  overflow: 'hidden',
  whiteSpace: 'nowrap',
  '&.is-selected': {
    bg: 'actionListItem.default.activeBg',
    '&::after': {
      position: 'absolute',
      top: 'calc(50% - 12px)',
      left: '-8px',
      width: '4px',
      height: '24px',
      content: "''",
      bg: 'rgb(9, 105, 218)',
      borderRadius: '6px',
    },
  },
  '&:not(.is-selected):hover': {
    bg: 'actionListItem.default.activeBg',
    '&::after': {
      position: 'absolute',
      top: 'calc(50% - 12px)',
      left: '-8px',
      width: '4px',
      height: '24px',
      content: "''",
      bg: 'rgb(9, 105, 218)',
      borderRadius: '6px',
    },
  },
}

export const SuggestionsListItem: React.FC<SuggestionsListItemProps> = (props: SuggestionsListItemProps) => {
  const computedProps = {
    ref: props.selectedItemRef,
    className: props.selected ? 'is-selected' : '',
    onClick: (e: React.MouseEvent<HTMLLIElement>) => {
      props.onSelect()
      // This prevents the suggestions from closing due to an outside click.
      e.stopPropagation()
    },
    id: props.id,
    role: 'option',
    'aria-selected': props.selected,
    tabIndex: -1,
    ...testIdProps(props.testId),
  }

  if (props.renderItem) {
    return (
      <Box as="li" sx={suggestionsListItemStyle} {...computedProps}>
        {props.renderItem()}
      </Box>
    )
  }

  if (props.asHTML) {
    return (
      <Box as="li" sx={suggestionsListItemStyle} {...computedProps}>
        <SanitizedHtml>{props.value}</SanitizedHtml>
      </Box>
    )
  } else {
    return (
      <Box as="li" sx={suggestionsListItemStyle} {...computedProps}>
        {props.value}
      </Box>
    )
  }
}

SuggestionsListItem.displayName = 'SuggestionsListItem'
